import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { ResolvedTheme, ThemePreference } from '@/types/ui'

/**
 * 深浅主题（AGENTS.md 4.2）。
 *
 * 策略是 Tailwind 的 `darkMode: 'class'` —— 在 `<html>` 上加/去 `dark` 类。
 * 选 class 而不是 media 的原因就是要支持**手动切换**，媒体查询做不到。
 *
 * 三档而不是两档：`system` 是有意义的第三态。只有 light/dark 的话，用户想
 * 「跟随系统」就只能去清 localStorage。
 *
 * 首帧的白闪由 index.html 里的内联脚本处理（它读同一个 STORAGE_KEY）——
 * React 挂载之前类名就已经是对的，这里只负责后续变更。
 */

const STORAGE_KEY = 'modsite.theme'

interface ThemeContextValue {
  /** 用户的选择，含 `system`。 */
  preference: ThemePreference
  /** 实际生效的主题，`system` 已解析。 */
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
  /** 在 light / dark 之间切换（从 system 出发时，切到与当前实际相反的那个）。 */
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // 隐私模式下 localStorage 会抛异常，用默认浅色即可。
  }
  // 从未选择过时默认浅色，不跟随系统 —— 与 index.html 里的首帧脚本保持一致。
  return 'light'
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference)
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme)

  // 跟随系统时要对系统变化实时反应，所以订阅始终挂着（成本极低），
  // 而不是只在 preference === 'system' 时订阅 —— 否则切回 system 会慢一拍。
  useEffect(() => {
    const list = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light')
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [])

  const resolved: ResolvedTheme = preference === 'system' ? system : preference

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark')
    // 让浏览器原生控件（滚动条、表单控件、输入法候选框）跟随主题。
    document.documentElement.style.colorScheme = resolved
  }, [resolved])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // 存不下就只在本次会话生效，不影响可用性。
    }
  }, [])

  const toggle = useCallback(() => {
    setPreferenceState((current) => {
      const next: ThemePreference =
        (current === 'system' ? systemTheme() : current) === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        /* 同上 */
      }
      return next
    })
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, toggle }),
    [preference, resolved, setPreference, toggle],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme 必须在 <ThemeProvider> 内使用')
  return context
}
